import type { Ticker, TickerCallback } from 'pixi.js';
import type { TimelineSettings } from './BaseTimeline';
import { ConcurrentTimeline } from './ConcurrentTimeline';

export interface TickerTimelineSettings extends TimelineSettings {
    onTick?: TickerCallback<unknown>;
}

/**
 * TickerTimeline uses the Ticker's update mechanism to evaluate the current time. It attaches play, pause and stop to
 * the ticker's start and stop functionality.
 *
 * play() is asynchronous and will cause the timeline to start playing on the next tick/update.
 * pause() and stop() are synchronous and will cause the timeline to stop playback immediately.
 */
export class TickerTimeline extends ConcurrentTimeline {
    #ticker: Ticker | null = null;

    constructor(ticker: Ticker, settings: TickerTimelineSettings = {}) {
        super(settings);

        this.name = 'TickerTimeline';
        this._playing = false;
        this._playRequested = false;
        this._startPlayAtTime = 0;

        this.#initTicker(ticker, settings.onTick);
    }

    #initTicker(ticker: Ticker, onTick?: TickerCallback<unknown>) {
        if (!ticker) {
            throw Error('PIXI.Ticker object is missing.');
        }
        this.#ticker = ticker;
        this.#ticker.add(this._tickerUpdate, this);

        if (onTick) {
            this.#ticker.add(onTick);
        }

        this.#ticker.stop();
    }

    startTicker() {
        this.#ticker?.start();
    }

    stopTicker() {
        this.#ticker?.stop();
    }

    /**
     * Manually advance the ticker by a single update, for tests/tools that need to force a frame.
     * @internal
     */
    _tick() {
        this.#ticker?.update();
    }

    /** Whether the ticker's render loop is running. Independent of playing() — it keeps running while paused. */
    get tickerRunning(): boolean {
        return this.#ticker?.started ?? false;
    }

    reset() {
        this.currentTime = 0;
        this.#clearRequests();
    }

    override play() {
        this.#clearRequests();
        this._playRequested = true;
        this._startPlayAtTime = this.currentTime;
    }

    pause() {
        this._playing = false;
        this.emit('pause');
    }

    stop() {
        this.pause();

        // Resetting the head to 0 here (rather than on play) avoids a seek-to-0 delay when starting playback, which
        // matters for video. Note that reading currentTime emits one more update event.
        this.reset();
    }

    override playing() {
        return this._playing || this._playRequested;
    }

    /** @internal */
    _play() {
        this._playing = true;
        this.#clearRequests();
    }

    #clearRequests() {
        this._playRequested = false;
    }

    /**
     * Hooked up to the ticker's update event. The first tick after a play request runs at the current time; subsequent
     * ticks advance currentTime by the ticker's deltaMS (capped by minFPS).
     * @internal
     */
    _tickerUpdate() {
        if (this._playing) {
            this.currentTime += this.#ticker?.deltaMS ?? 0;
        } else if (this._playRequested) {
            this._play();
            this.currentTime = this._startPlayAtTime;
        }
    }

    override destroy() {
        if (this.#ticker) {
            this.#ticker.destroy();
            this.#ticker = null;
        }
        super.destroy();
    }
}
